import { ReactNode, useState } from "react";

import InfoIcon from "src/assets/icons/SsqTitleIcon/info.svg?react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { Button, Stack, Typography } from "@mui/material";
import { ButtonTabs } from "@foodpilot/foods";
import { useNavigate } from "react-router";
import { GenericBox } from "src/components/GenericBox";
import CreateOutlined from "@mui/icons-material/CreateOutlined";

import { Packaging } from "src/api/client-api/packagings/useGetOnePackaging";
import { InformationSummary } from "src/components/Summary/PackagingSummary/InformationSummary";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { usePackagingContext } from "../PackagingContext";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { PackagingInformationsV2 } from "./PackagingInformationsV2";

type ModifyButtonProps = {
    packaging: Packaging;
    pageNumber: number;
};
const ModifyButton = (props: ModifyButtonProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { selectedCompany } = useFoodpilotContext();
    const { packaging, pageNumber } = props;

    if (selectedCompany === null) return null;

    const modifyText = t("modifier");
    if (packaging.canModify === false) {
        return null;
    }

    return (
        <Button
            variant="primary"
            startIcon={<CreateOutlined />}
            sx={{ marginRight: "24px", borderRadius: "44px" }}
            onClick={() => {
                const url = URLHandler.packaging.edit(selectedCompany.id, packaging.id, pageNumber);
                navigate(url);
            }}
        >
            {modifyText}
        </Button>
    );
};

type Page = {
    title: string;
    icon?: ReactNode;
    content: ReactNode | ReactNode[];
};
const LegacyInformations = () => {
    const { t } = useTranslation();
    const { packaging } = usePackagingContext();

    const [currentPage, setCurrentPage] = useState(0);

    if (packaging === undefined) return null;

    const allPages: Page[] = [
        {
            title: t("general_information"),
            icon: <InfoIcon />,
            content: (
                <GenericBox
                    headerIcon={<InfoOutlined />}
                    headerText={t("general_information")}
                    controls={<ModifyButton pageNumber={0} packaging={packaging} />}
                >
                    <InformationSummary packaging={packaging} />
                </GenericBox>
            ),
        },
    ];

    const choices = allPages.map((p, index) => (
        <Stack key={index} direction="row" alignItems="center" gap="8px">
            {p.icon}
            {p.title}
        </Stack>
    ));

    return (
        <Stack gap="28px">
            <Typography variant="h2">{t("informations")}</Typography>
            <ButtonTabs choices={choices} onClick={(choiceIndex) => setCurrentPage(choiceIndex)} />
            {allPages[currentPage].content}
        </Stack>
    );
};

export const Informations = () => {
    const isProductUxV2 = useIsProductUxV2();

    return isProductUxV2 ? <PackagingInformationsV2 /> : <LegacyInformations />;
};
